// Latency instrumentation for the realtime call.
//
// The bot's voice and its avatar travel on two different legs:
//   audio: pipecat → Pipecat WebRTC connection → <audio> element
//   video: pipecat → LiveAvatar websocket → HeyGen render → LiveKit → <video>
//
// Everything logs to the console with a `[latency]` prefix. Per bot turn you
// get the bot_started_speaking event time, when sound actually became audible,
// and avatar video freezes; every 5s you get a side-by-side network report for
// the two legs. The pipecat server logs matching `[latency]` lines (bot.py,
// live_avatar.py), so a slow turn can be attributed to the model/TTS, the
// audio leg, or the video leg.

const SOUND_RMS_THRESHOLD = 0.01
const SOUND_GAP_MS = 400 // silence shorter than this is a pause, not a new speech onset
const FREEZE_MS = 500
const STATS_INTERVAL_MS = 5000

export function createLatencyProbe() {
  const startedAt = performance.now()
  let stopped = false

  const ts = () => `${((performance.now() - startedAt) / 1000).toFixed(1)}s`
  const log = (message) => console.log(`[latency ${ts()}] ${message}`)
  const ms = (delta) => `${Math.round(delta)}ms`

  // --- turn tracking (RTVI events arriving on the pipecat data channel) ---
  let turn = 0
  let userStoppedAt = null
  let botStartedAt = null
  let waitingForAudible = false

  const markUserStartedSpeaking = () => log('user started speaking')

  const markUserStoppedSpeaking = () => {
    userStoppedAt = performance.now()
    log('user stopped speaking — waiting for the bot')
  }

  const markBotStartedSpeaking = () => {
    turn += 1
    botStartedAt = performance.now()
    waitingForAudible = true
    const suffix = userStoppedAt != null ? ` (${ms(botStartedAt - userStoppedAt)} after user stopped)` : ''
    log(`turn ${turn}: bot_started_speaking event${suffix}`)
  }

  const markBotStoppedSpeaking = () => {
    if (waitingForAudible) log(`turn ${turn}: bot stopped speaking but audio never became audible — audio leg problem`)
    log(`turn ${turn}: bot_stopped_speaking event`)
    waitingForAudible = false
    userStoppedAt = null
  }

  // --- audio audibility: is sound actually coming out of the audio leg? ---
  let audioCtx = null
  let analyser = null
  let audioData = null
  let audioTimer = null
  let audioTrack = null
  let lastSoundAt = 0

  const attachBotAudioTrack = (track) => {
    if (!track || track === audioTrack) return
    try {
      audioTrack = track
      if (audioCtx && audioCtx.state !== 'closed') audioCtx.close().catch(() => {})
      const Ctx = window.AudioContext || window.webkitAudioContext
      audioCtx = new Ctx()
      analyser = audioCtx.createAnalyser()
      analyser.fftSize = 1024
      audioCtx.createMediaStreamSource(new MediaStream([track])).connect(analyser)
      audioData = new Float32Array(analyser.fftSize)
      clearInterval(audioTimer)
      audioTimer = setInterval(sampleAudioLevel, 50)
      log('monitoring bot audio output level')
    } catch (e) {
      console.warn('[latency] audio level monitor failed:', e)
    }
  }

  const sampleAudioLevel = () => {
    if (stopped || !analyser) return
    analyser.getFloatTimeDomainData(audioData)
    let sum = 0
    for (let i = 0; i < audioData.length; i++) sum += audioData[i] * audioData[i]
    const rms = Math.sqrt(sum / audioData.length)
    if (rms <= SOUND_RMS_THRESHOLD) return
    const now = performance.now()
    const onset = now - lastSoundAt > SOUND_GAP_MS
    lastSoundAt = now
    if (onset && waitingForAudible) {
      waitingForAudible = false
      const parts = []
      if (botStartedAt != null) parts.push(`${ms(now - botStartedAt)} after the bot_started_speaking event`)
      if (userStoppedAt != null) parts.push(`${ms(now - userStoppedAt)} after user stopped`)
      log(`turn ${turn}: bot audio AUDIBLE (${parts.join(', ') || 'no reference event'})`)
    }
  }

  // --- avatar video frames: is the video leg actually delivering? ---
  let videoEl = null
  let livekitTrack = null
  let framesInWindow = 0

  const attachAvatarVideo = (el, track) => {
    if (track) livekitTrack = track
    startStatsLoop()
    if (!el || el === videoEl) return
    videoEl = el
    if (typeof el.requestVideoFrameCallback !== 'function') {
      log('avatar video attached (requestVideoFrameCallback unsupported — frame timing disabled)')
      return
    }
    let lastFrameAt = 0
    let firstFrameLogged = false
    const onFrame = (now) => {
      if (stopped || videoEl !== el) return
      framesInWindow += 1
      if (!firstFrameLogged) {
        firstFrameLogged = true
        log('avatar video: first frame rendered')
      } else if (lastFrameAt && now - lastFrameAt > FREEZE_MS) {
        log(`avatar video: FROZE for ${ms(now - lastFrameAt)} — video leg stalled (audio may have kept playing)`)
      }
      lastFrameAt = now
      el.requestVideoFrameCallback(onFrame)
    }
    el.requestVideoFrameCallback(onFrame)
  }

  // --- side-by-side network stats for the two legs ---
  let statsTimer = null
  let pipecatPc = null
  const prevStats = {}

  const setPipecatClient = (client) => {
    pipecatPc = findPeerConnection(client)
    log(pipecatPc
      ? 'pipecat RTCPeerConnection found — audio-leg network stats enabled'
      : 'could not locate pipecat RTCPeerConnection — audio-leg network stats disabled')
    startStatsLoop()
  }

  const startStatsLoop = () => {
    if (statsTimer || stopped) return
    statsTimer = setInterval(reportPathStats, STATS_INTERVAL_MS)
  }

  const reportPathStats = async () => {
    if (stopped) return
    const [audioReport, videoReport] = await Promise.all([
      pipecatPc ? pipecatPc.getStats().catch(() => null) : null,
      livekitTrack?.getRTCStatsReport ? livekitTrack.getRTCStatsReport().catch(() => null) : null,
    ])
    const audio = summarizeLeg(audioReport, 'audio', prevStats)
    const video = summarizeLeg(videoReport, 'video', prevStats)
    const renderFps = (framesInWindow / (STATS_INTERVAL_MS / 1000)).toFixed(0)
    framesInWindow = 0
    if (!audio && !video && !videoEl) return
    const audioPart = audio
      ? `rtt=${audio.rtt} jitter=${audio.jitter} jbuf=${audio.jbuf} lost=+${audio.lostDelta}`
      : 'n/a'
    const videoPart = video
      ? `render=${renderFps}fps recv=${video.fps ?? '?'}fps jbuf=${video.jbuf} freezes=+${video.freezeDelta} lost=+${video.lostDelta}`
      : videoEl
        ? `render=${renderFps}fps (no RTC stats)`
        : 'n/a'
    log(`legs — audio[pipecat]: ${audioPart} | video[livekit]: ${videoPart}`)
  }

  const mark = (message) => log(message)

  const stop = () => {
    if (stopped) return
    stopped = true
    clearInterval(audioTimer)
    clearInterval(statsTimer)
    if (audioCtx && audioCtx.state !== 'closed') audioCtx.close().catch(() => {})
    log('latency probe stopped')
  }

  return {
    mark,
    stop,
    markUserStartedSpeaking,
    markUserStoppedSpeaking,
    markBotStartedSpeaking,
    markBotStoppedSpeaking,
    attachBotAudioTrack,
    attachAvatarVideo,
    setPipecatClient,
  }
}

function summarizeLeg(report, kind, prevStats) {
  if (!report) return null
  let inbound = null
  let rttMs = null
  report.forEach((stat) => {
    if (stat.type === 'inbound-rtp' && stat.kind === kind) inbound = stat
    if (stat.type === 'candidate-pair' && stat.state === 'succeeded' && stat.currentRoundTripTime != null) {
      rttMs = stat.currentRoundTripTime * 1000
    }
  })
  if (!inbound) return null
  const prev = prevStats[kind] || {}
  const jbufDelta = (inbound.jitterBufferDelay ?? 0) - (prev.jitterBufferDelay ?? 0)
  const jbufCount = (inbound.jitterBufferEmittedCount ?? 0) - (prev.jitterBufferEmittedCount ?? 0)
  const summary = {
    rtt: rttMs != null ? `${Math.round(rttMs)}ms` : '?',
    jitter: inbound.jitter != null ? `${Math.round(inbound.jitter * 1000)}ms` : '?',
    jbuf: jbufCount > 0 ? `${Math.round((jbufDelta / jbufCount) * 1000)}ms` : '?',
    lostDelta: (inbound.packetsLost ?? 0) - (prev.packetsLost ?? 0),
    freezeDelta: (inbound.freezeCount ?? 0) - (prev.freezeCount ?? 0),
    fps: inbound.framesPerSecond != null ? Math.round(inbound.framesPerSecond) : null,
  }
  prevStats[kind] = {
    jitterBufferDelay: inbound.jitterBufferDelay ?? 0,
    jitterBufferEmittedCount: inbound.jitterBufferEmittedCount ?? 0,
    packetsLost: inbound.packetsLost ?? 0,
    freezeCount: inbound.freezeCount ?? 0,
  }
  return summary
}

// The pipecat client doesn't expose its RTCPeerConnection, so walk the object
// graph a few levels deep to find it. Debug-only; returns null if not found.
function findPeerConnection(root) {
  if (typeof RTCPeerConnection === 'undefined') return null
  const seen = new Set()
  let frontier = [root]
  for (let depth = 0; depth < 5 && frontier.length > 0; depth++) {
    const next = []
    for (const obj of frontier) {
      if (!obj || typeof obj !== 'object' || seen.has(obj)) continue
      seen.add(obj)
      if (obj instanceof RTCPeerConnection) return obj
      if (obj instanceof Node) continue
      for (const key of Object.keys(obj)) {
        try {
          const value = obj[key]
          if (value && typeof value === 'object') next.push(value)
        } catch {
          /* getters may throw */
        }
      }
    }
    frontier = next
  }
  return null
}
